import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import React from "react"
import dayjs from "dayjs";
import { IAdminUser } from "../types/api/user";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function removeSpecialChars(key: string) {
  return key?.trim().toLowerCase()?.replace(/[^a-z0-9 _-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

export function generateFieldId(label: string) {
  return label
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, "")   
    .replace(/\s+/g, "_")         
    .replace(/_+/g, "_");        
}


export function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("File could not be converted to base64 string"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
}

export const renderTableText = (value: string, className?: string) => value ? <span className={cn(className)}>{value}</span> : <span>NA</span>

export const handleTableSortingData = (sorter: any, setSort: any, curr_sort: any, refetch: any, qData: any) => {
  const updateSortAndRefetch = (newSort: any) => {
      setSort(newSort);
      if (typeof refetch === 'function') {
          refetch({ ...qData, sort: JSON.stringify(newSort) });
      }
  };
  // Multi-sort mode (array)
  if (Array.isArray(sorter) && sorter.length > 0) {
      const sortKeys = sorter
          .filter((item: any) => item.order)
          .map((item: any) => ({
              column: item.field,
              desc: item.order === 'ascend' ? 0 : 1,
          }));
      return updateSortAndRefetch(sortKeys);
  }
  // Single-sort mode (object)
  const { order, field } = sorter || {};
  if (!field) return;

  const newSortItem = {
      column: field,
      desc: order === 'ascend' ? 0 : 1,
  };

  if (order) {
      updateSortAndRefetch([newSortItem]);
  } else {
      // Sort removed for this column
      const filtered = curr_sort.filter((x: any) => x.column !== field);
      return updateSortAndRefetch(filtered);
  }
};

export const renderWithoutUnderscore = (val: string) =>{
  return val.split('_').join(' ')
}

export function isValidLink(text: string): boolean {
  try {
    new URL(text);
    return true; // valid
  } catch {
    return false; // invalid
  }
}

export const getCroppedImg = (
  file: File,
  croppedAreaPixels: {
    width: number;
    height: number;
    x: number;
    y: number;
  }
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = URL.createObjectURL(file);
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Failed to get 2D context"));
        return;
      }

      // IMPORTANT — keep final output same as crop area
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    };

    image.onerror = (err) => reject(err);
  });
};

export function toSentenceCase(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}


export function formatEnum(value: string): string {
  if (!value) return "";

  return value
    .toLowerCase()
    .replace(/_/g, " ") // replace underscores with spaces
    .split(" ")
    .map(word =>
      word.length > 0
        ? word.charAt(0).toUpperCase() + word.slice(1)
        : ""
    )
    .join(" ");
}

export const setUserData = (data: any) => {
  if (data?.accessToken) {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("admin_token", data.accessToken);
      window.localStorage.setItem("authorization", data.accessToken);
      if (data.type) window.localStorage.setItem("userType", data.type);
    }
    setCookie("token", data.accessToken);
  }
};

export const getToken = () => {
  if (typeof window !== "undefined") {
    const localToken =
      window.localStorage.getItem("admin_token") ||
      window.localStorage.getItem("authorization");
    if (localToken) return localToken;
  }

  if (typeof document === "undefined") return null;

  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1] || null
  );
};

export const setCookie = (name: string, value: string, days: number = 30) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("admin_token", value);
    window.localStorage.setItem("authorization", value);
  }
  const maxAge = days * 24 * 60 * 60; // 30 days
  document.cookie = `${name}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
};

export const clearCookie = (name: string) => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("admin_token");
    window.localStorage.removeItem("authorization");
    window.localStorage.removeItem("userType");
  }
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};

// A separate, deliberately non-secret cookie (no token, no session data -
// just a flag) scoped to the whole .artparks.co.uk domain, not just
// admin.artparks.co.uk. Lets the public site (a different subdomain,
// separate auth entirely) know "someone is logged into admin right now"
// so it can show an "Edit in Admin" link on sculpture pages - the actual
// admin login still gates anything real, this cookie only controls
// whether that link is shown at all (Oliver, Sept 2026). Only set/cleared
// in production - admin.artparks.co.uk/artparks.co.uk aren't the same
// registrable domain as the local dev hosts, so a domain-scoped cookie
// would just be silently rejected by the browser there anyway.
const EDIT_HINT_COOKIE = "artparks_admin_hint";
export const setEditHintCookie = () => {
  if (process.env.NODE_ENV !== "production" || typeof document === "undefined") return;
  document.cookie = `${EDIT_HINT_COOKIE}=1; domain=.artparks.co.uk; path=/; max-age=${30 * 24 * 60 * 60}; SameSite=Lax`;
};
export const clearEditHintCookie = () => {
  if (process.env.NODE_ENV !== "production" || typeof document === "undefined") return;
  document.cookie = `${EDIT_HINT_COOKIE}=; domain=.artparks.co.uk; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
};
  

 
  // utils/getSingleImageUrl.ts
export const getSingleImageUrl = (image: string | string[] | undefined | null): string => {
  if (Array.isArray(image)) {
    return image[0] || "";
  }
  return image || "";
};

export function checkRightAccess(key?: string , userInfo? :  IAdminUser ) {

  // PLATFORM_SUPER_ADMIN routes: only visible to PlatformAdmin users
  if (key === "PLATFORM_SUPER_ADMIN") {
    return userInfo?.type === "PlatformAdmin";
  }

  // Platform admin has full access to all other routes
  if (userInfo?.type === "PlatformAdmin") return true;

  // Company super admin has full access to company routes
  if ((userInfo?.type || "SuperAdmin") === "SuperAdmin") return true;

  // If key not passed, just check if Admin exists
  if (!key) return !!userInfo;

  // Check access rights
  return Array.isArray(userInfo?.rights) && userInfo?.rights.includes(key);
}






export function renderIcon(icon: unknown) {
  return icon as React.FC<React.SVGProps<SVGSVGElement>>;
}

export const formatDate = (date?: dayjs.Dayjs) => date ? date.format("YYYY-MM-DD") : null;


export const extractDirtyChanges = (
  dirty: any,
  values: any,
  defaults: any
) => {
  const result = {
    old: {},
    new: {},
  };

  const setNested = (obj: any, path: string[], value: any) => {
    let curr = obj;
    path.forEach((key, i) => {
      if (i === path.length - 1) {
        curr[key] = value;
      } else {
        curr[key] = curr[key] ?? {};
        curr = curr[key];
      }
    });
  };

  const walk = (
    dirtyNode: any,
    valueNode: any,
    defaultNode: any,
    path: string[] = []
  ) => {
    Object.keys(dirtyNode || {}).forEach((key) => {
      if (dirtyNode[key] === true) {
        setNested(result.old, path.concat(key), defaultNode?.[key] ?? null);
        setNested(result.new, path.concat(key), valueNode?.[key] ?? null);
        return;
      }

      if (typeof dirtyNode[key] === "object") {
        walk(
          dirtyNode[key],
          valueNode?.[key],
          defaultNode?.[key],
          path.concat(key)
        );
      }
    });
  };

  walk(dirty, values, defaults);

  return result;
};
